import { TableColumnType } from 'antd';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

export interface PageListColumnOrderConfig {
  enabled: boolean;
  persist?: boolean;
  storageKey?: string;
  fixedColumnKeys?: string[];
}

export interface PageListColumnOrderState<T> {
  isColumnOrderEnabled: boolean;
  orderedColumns: TableColumnType<T>[];
  columnOrder: string[];
  fixedColumnKeys: string[];
  setColumnOrder: Dispatch<SetStateAction<string[]>>;
  handleResetColumnOrder: () => void;
  hasCustomColumnOrder: boolean;
}

const getColumnKey = (column: TableColumnType<any>) => {
  return column.key === undefined ? undefined : String(column.key);
};

const getColumnKeys = (columns: TableColumnType<any>[]) => {
  return columns.map(getColumnKey).filter((key): key is string => Boolean(key));
};

const isSameColumnOrder = (first: string[], second: string[]) => {
  return (
    first.length === second.length &&
    first.every((key, index) => key === second[index])
  );
};

const normalizeColumnOrder = (
  columnKeys: string[],
  persistedColumnKeys: string[],
  fixedColumnKeys: string[]
) => {
  const validFixedColumnKeys = fixedColumnKeys.filter(key =>
    columnKeys.includes(key)
  );
  const movableColumnKeys = columnKeys.filter(
    key => !validFixedColumnKeys.includes(key)
  );
  const persistedMovableColumnKeys = persistedColumnKeys.filter(
    (key, index) =>
      movableColumnKeys.includes(key) &&
      persistedColumnKeys.indexOf(key) === index
  );
  const missingMovableColumnKeys = movableColumnKeys.filter(
    key => !persistedMovableColumnKeys.includes(key)
  );
  const normalizedMovableColumnKeys = [
    ...persistedMovableColumnKeys,
    ...missingMovableColumnKeys,
  ];
  let movableColumnIndex = 0;

  return columnKeys.map(columnKey => {
    if (validFixedColumnKeys.includes(columnKey)) return columnKey;

    const movableColumnKey = normalizedMovableColumnKeys[movableColumnIndex];
    movableColumnIndex += 1;

    return movableColumnKey;
  });
};

const getPersistedColumnOrder = (
  storageKey: string | undefined,
  columnKeys: string[],
  fixedColumnKeys: string[]
) => {
  if (!storageKey) return columnKeys;

  try {
    const storedValue = localStorage.getItem(storageKey);

    if (!storedValue) return columnKeys;

    const parsedValue = JSON.parse(storedValue);

    if (!Array.isArray(parsedValue)) return columnKeys;

    return normalizeColumnOrder(
      columnKeys,
      parsedValue.filter((key): key is string => typeof key === 'string'),
      fixedColumnKeys
    );
  } catch {
    return columnKeys;
  }
};

const orderColumns = <T>(
  columns: TableColumnType<T>[],
  columnOrder: string[]
) => {
  const columnPosition = new Map(
    columnOrder.map((columnKey, index) => [columnKey, index])
  );

  return [...columns].sort((firstColumn, secondColumn) => {
    const firstColumnKey = getColumnKey(firstColumn);
    const secondColumnKey = getColumnKey(secondColumn);
    const firstColumnPosition =
      firstColumnKey === undefined
        ? Number.MAX_SAFE_INTEGER
        : (columnPosition.get(firstColumnKey) ?? Number.MAX_SAFE_INTEGER);
    const secondColumnPosition =
      secondColumnKey === undefined
        ? Number.MAX_SAFE_INTEGER
        : (columnPosition.get(secondColumnKey) ?? Number.MAX_SAFE_INTEGER);

    return firstColumnPosition - secondColumnPosition;
  });
};

export const usePageListColumnOrder = <T>(
  columns: TableColumnType<T>[],
  columnOrderConfig?: PageListColumnOrderConfig
): PageListColumnOrderState<T> => {
  const isColumnOrderEnabled = Boolean(columnOrderConfig?.enabled);
  const columnKeys = useMemo(() => getColumnKeys(columns), [columns]);
  const fixedColumnKeys = useMemo(
    () =>
      (columnOrderConfig?.fixedColumnKeys || []).filter(key =>
        columnKeys.includes(key)
      ),
    [columnKeys, columnOrderConfig?.fixedColumnKeys]
  );
  const shouldPersistColumnOrder = Boolean(
    columnOrderConfig?.persist && columnOrderConfig.storageKey
  );
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (
      !isColumnOrderEnabled ||
      !shouldPersistColumnOrder ||
      !columnOrderConfig?.storageKey
    ) {
      return columnKeys;
    }

    return getPersistedColumnOrder(
      columnOrderConfig.storageKey,
      columnKeys,
      fixedColumnKeys
    );
  });

  useEffect(() => {
    if (!isColumnOrderEnabled) return;

    setColumnOrder(prev => {
      const nextColumnOrder = normalizeColumnOrder(
        columnKeys,
        prev,
        fixedColumnKeys
      );

      if (isSameColumnOrder(nextColumnOrder, prev)) return prev;

      return nextColumnOrder;
    });
  }, [columnKeys, fixedColumnKeys, isColumnOrderEnabled]);

  useEffect(() => {
    if (
      !isColumnOrderEnabled ||
      !shouldPersistColumnOrder ||
      !columnOrderConfig?.storageKey
    ) {
      return;
    }

    if (isSameColumnOrder(columnOrder, columnKeys)) {
      localStorage.removeItem(columnOrderConfig.storageKey);
      return;
    }

    localStorage.setItem(
      columnOrderConfig.storageKey,
      JSON.stringify(columnOrder)
    );
  }, [
    columnKeys,
    columnOrder,
    fixedColumnKeys,
    columnOrderConfig?.storageKey,
    isColumnOrderEnabled,
    shouldPersistColumnOrder,
  ]);

  const orderedColumns = useMemo(() => {
    if (!isColumnOrderEnabled) return columns;

    return orderColumns(columns, columnOrder);
  }, [columnOrder, columns, isColumnOrderEnabled]);

  const hasCustomColumnOrder = useMemo(() => {
    return !isSameColumnOrder(columnOrder, columnKeys);
  }, [columnKeys, columnOrder]);

  const handleResetColumnOrder = () => {
    setColumnOrder(columnKeys);

    if (shouldPersistColumnOrder && columnOrderConfig?.storageKey) {
      localStorage.removeItem(columnOrderConfig.storageKey);
    }
  };

  return {
    isColumnOrderEnabled,
    orderedColumns,
    columnOrder,
    fixedColumnKeys,
    setColumnOrder,
    handleResetColumnOrder,
    hasCustomColumnOrder,
  };
};
